import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Handshake, KanbanSquare, Plus, Table2, Trash2 } from 'lucide-react';
import type { BoardColumn, Deal, PipelineMeta, Stage, TakeoverResult } from '@epasu/core';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DealDialog } from '@/components/DealDialog';
import { LostDealNotesDialog } from '@/components/LostDealNotesDialog';
import { crm } from '@/lib/crm-client';
import { formatEuro } from '@/lib/utils';
import { TakeoverDialog } from '@/pages/akquise/TakeoverDialog';

type View = 'kanban' | 'table';

/**
 * Das Akquise-Brett: von der ersten positiven Antwort bis zur Zusage.
 *
 * Die Verloren-Spalte zeigt hier keine Karten (`includeLost: false`) — die
 * Absagen stehen hinter dem Filter „Verloren“ (`onZeigeVerlorene`). Die Spalte
 * selbst bleibt als Ablageziel: wer eine Karte darauf zieht, sagt ab und wird
 * gleich nach dem Grund gefragt.
 *
 * An der Zusage-Spalte sitzt der einzige Übergang des Systems: „Als Kunde
 * übernehmen“ macht aus der Firma einen Kunden (siehe `TakeoverDialog`).
 */
export function PipelinePage({ onZeigeVerlorene }: { onZeigeVerlorene?: () => void } = {}) {
  const [pipes, setPipes] = useState<PipelineMeta[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [board, setBoard] = useState<BoardColumn[]>([]);
  const [view, setView] = useState<View>('kanban');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<{ deal: Deal | null; stage: Stage } | null>(
    null,
  );
  const [deleting, setDeleting] = useState<Deal | null>(null);
  /** Deal, der gerade als verloren markiert wurde — das Notizfeld geht auf. */
  const [verloren, setVerloren] = useState<{ deal: Deal; stage: Stage } | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  /** Karte, die gerade übernommen werden soll — der Dialog geht auf. */
  const [uebernahme, setUebernahme] = useState<Deal | null>(null);
  /** Ergebnis der letzten Übernahme, als Hinweis über dem Brett. */
  const [uebernommen, setUebernommen] = useState<
    (TakeoverResult & { organizationId: string; organizationName: string }) | null
  >(null);

  useEffect(() => {
    void crm.pipelines
      .listWithMeta()
      .then((list) => {
        setPipes(list);
        const school = list.find((p) => p.audienceKey === 'schools') ?? list[0];
        setActiveId(school?.id ?? null);
        if (!school) setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
        setLoading(false);
      });
  }, []);

  const loadBoard = useCallback(async (pipelineId: string) => {
    const frisch = await crm.pipelines.board(pipelineId, { includeLost: false });
    setBoard(frisch);
    setLoading(false);
    return frisch;
  }, []);

  useEffect(() => {
    if (activeId) void loadBoard(activeId);
  }, [activeId, loadBoard]);

  const totalDeals = useMemo(
    () => board.reduce((n, c) => n + c.deals.length, 0),
    [board],
  );
  const totalValue = useMemo(
    () => board.reduce((sum, c) => sum + c.deals.reduce((s, d) => s + d.valueCents, 0), 0),
    [board],
  );

  const active = pipes.find((p) => p.id === activeId);

  /**
   * Verschieben per Drag-and-drop.
   *
   * Die Ansicht wird sofort umgestellt und erst danach der Server gefragt —
   * andernfalls springt die Karte für einen Moment zurück. Schlägt der Aufruf
   * fehl, wird das Board neu geladen, damit die Anzeige nicht lügt.
   */
  const drop = async (dealId: string, toStageId: string) => {
    setDropTarget(null);
    setDragging(null);

    const from = board.find((c) => c.deals.some((d) => d.id === dealId));
    if (!from || from.stage.id === toStageId) return;
    const deal = from.deals.find((d) => d.id === dealId);
    if (!deal) return;

    setBoard((prev) =>
      prev.map((col) => {
        if (col.stage.id === from.stage.id) {
          return { ...col, deals: col.deals.filter((d) => d.id !== dealId) };
        }
        // Die Verloren-Spalte zeigt keine Karten: die Absage verlässt das Brett.
        if (col.stage.id === toStageId && !col.stage.isLost) {
          return { ...col, deals: [{ ...deal, stageId: toStageId }, ...col.deals] };
        }
        return col;
      }),
    );

    let gelungen = true;
    try {
      await crm.pipelines.moveDeal(dealId, toStageId);
    } catch (err) {
      gelungen = false;
      setError(err instanceof Error ? err.message : 'Verschieben fehlgeschlagen');
    }
    // In jedem Fall neu laden: beim Wechsel auf „Gewonnen“ legen Automations
    // Aufgaben an, und der Stand soll das widerspiegeln.
    if (activeId) await loadBoard(activeId);

    // Absage frisch markiert → gleich nach dem Grund fragen. Später trägt ihn
    // niemand mehr nach, und ohne ihn ist der Deal für eine zweite Ansprache
    // nur ein Name. Das Brett zeigt die Karte nicht mehr, also kommt sie aus
    // dem Stand vor dem Ziehen.
    const ziel = board.find((c) => c.stage.id === toStageId);
    if (gelungen && ziel?.stage.isLost) {
      setVerloren({ deal, stage: ziel.stage });
    }
  };

  /**
   * Nach Anlegen, Bearbeiten oder Löschen neu laden. Ist der Deal dabei in
   * einer Verloren-Stufe gelandet und hat noch keine Notiz, geht dasselbe Feld
   * auf wie beim Ziehen — der Weg über den Dialog soll nichts verschlucken.
   */
  const afterChange = async (dealId?: string) => {
    setEditing(null);
    setDeleting(null);
    if (activeId) await loadBoard(activeId);
    if (!dealId || !activeId) return;

    // Das Brett blendet Absagen aus; ob der Deal dort gelandet ist, zeigt nur
    // der volle Stand.
    const frisch = await crm.pipelines.board(activeId);
    const spalte = frisch.find((c) => c.deals.some((d) => d.id === dealId));
    const deal = spalte?.deals.find((d) => d.id === dealId);
    if (spalte?.stage.isLost && deal && !deal.notes?.trim()) {
      setVerloren({ deal, stage: spalte.stage });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{active?.name ?? 'Pipeline'}</h2>
          <p className="text-sm text-muted-foreground">
            {totalDeals} {totalDeals === 1 ? 'Deal' : 'Deals'}
            {totalValue > 0 && ` · ${formatEuro(totalValue)}`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
            {pipes.map((p) => (
              <button
                key={p.id}
                onClick={() => setActiveId(p.id)}
                className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                  p.id === activeId
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>

          <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
            <button
              onClick={() => setView('kanban')}
              aria-label="Kanban-Ansicht"
              className={`rounded-md p-1.5 ${view === 'kanban' ? 'bg-muted' : ''}`}
            >
              <KanbanSquare className="h-4 w-4" />
            </button>
            <button
              onClick={() => setView('table')}
              aria-label="Tabellenansicht"
              className={`rounded-md p-1.5 ${view === 'table' ? 'bg-muted' : ''}`}
            >
              <Table2 className="h-4 w-4" />
            </button>
          </div>

          <Button
            disabled={board.length === 0}
            onClick={() => {
              const first = board[0];
              if (first) setEditing({ deal: null, stage: first.stage });
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Neuer Deal
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {error}
        </Card>
      )}

      {uebernommen && (
        <Card className="flex flex-wrap items-center justify-between gap-3 border-success/40 bg-success/5 p-4 text-sm">
          <span>
            {uebernommen.alreadyCustomer
              ? `${uebernommen.organizationName} war schon Kunde — es wurde nichts doppelt angelegt.`
              : `${uebernommen.organizationName} ist jetzt Kunde.`}
            {uebernommen.project && ` Projekt „${uebernommen.project.title}“ angelegt.`}
          </span>
          <span className="flex items-center gap-3">
            {uebernommen.project && (
              <Link
                to={`/projekte/${uebernommen.project.id}`}
                className="font-medium text-brand hover:underline"
              >
                Zum Projekt
              </Link>
            )}
            <Link
              to={`/firmen/${uebernommen.organizationId}`}
              className="font-medium text-brand hover:underline"
            >
              Zur Firma
            </Link>
            <button
              className="text-muted-foreground hover:underline"
              onClick={() => setUebernommen(null)}
            >
              Schließen
            </button>
          </span>
        </Card>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Lade …</p>
      ) : view === 'kanban' ? (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {board.map((col) => (
            <div
              key={col.stage.id}
              className={`w-72 shrink-0 rounded-lg border bg-muted/30 p-3 transition-colors ${
                dropTarget === col.stage.id ? 'border-primary bg-primary/5' : ''
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDropTarget(col.stage.id);
              }}
              onDragLeave={() => setDropTarget((t) => (t === col.stage.id ? null : t))}
              onDrop={(e) => {
                e.preventDefault();
                const id = e.dataTransfer.getData('text/plain');
                if (id) void drop(id, col.stage.id);
              }}
            >
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium">{col.stage.name}</span>
                {!col.stage.isLost && (
                  <span className="text-xs text-muted-foreground">
                    {col.deals.length}
                  </span>
                )}
              </div>

              {col.stage.isLost ? (
                <div className="space-y-2 text-xs text-muted-foreground">
                  <p>Karte hierher ziehen, wenn abgesagt wurde.</p>
                  {onZeigeVerlorene && (
                    <button className="text-brand hover:underline" onClick={onZeigeVerlorene}>
                      Absagen ansehen
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {col.deals.map((deal) => (
                    <DealCard
                      key={deal.id}
                      deal={deal}
                      onTakeover={col.stage.isWon ? () => setUebernahme(deal) : undefined}
                      dragging={dragging === deal.id}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', deal.id);
                        e.dataTransfer.effectAllowed = 'move';
                        setDragging(deal.id);
                      }}
                      onDragEnd={() => setDragging(null)}
                      onEdit={() => setEditing({ deal, stage: col.stage })}
                      onDelete={() => setDeleting(deal)}
                    />
                  ))}

                  <button
                    className="w-full rounded-md border border-dashed py-2 text-xs text-muted-foreground hover:bg-muted/50"
                    onClick={() => setEditing({ deal: null, stage: col.stage })}
                  >
                    + Deal in „{col.stage.name}“
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <THead>
              <TR>
                <TH>Titel</TH>
                <TH>Stufe</TH>
                <TH>Wert</TH>
                <TH>Abschluss</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {board.flatMap((col) =>
                col.deals.map((deal) => (
                  <TR key={deal.id}>
                    <TD className="font-medium">{deal.title}</TD>
                    <TD>{col.stage.name}</TD>
                    <TD>{deal.valueCents ? formatEuro(deal.valueCents) : '—'}</TD>
                    <TD>
                      {deal.expectedCloseDate
                        ? new Date(deal.expectedCloseDate).toLocaleDateString('de-DE')
                        : '—'}
                    </TD>
                    <TD>
                      <div className="flex justify-end gap-1">
                        {col.stage.isWon && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setUebernahme(deal)}
                          >
                            <Handshake className="mr-1.5 h-3.5 w-3.5" />
                            Als Kunde übernehmen
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditing({ deal, stage: col.stage })}
                        >
                          Bearbeiten
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`${deal.title} löschen`}
                          onClick={() => setDeleting(deal)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TD>
                  </TR>
                )),
              )}
            </TBody>
          </Table>
          {totalDeals === 0 && (
            <p className="p-6 text-center text-sm text-muted-foreground">
              Noch keine Deals in dieser Pipeline.
            </p>
          )}
        </Card>
      )}

      {editing && activeId && (
        <DealDialog
          deal={editing.deal}
          stage={editing.stage}
          stages={board.map((c) => c.stage)}
          pipelineId={activeId}
          onClose={() => setEditing(null)}
          onSaved={(dealId) => void afterChange(dealId)}
        />
      )}

      {uebernahme && (
        <TakeoverDialog
          deal={uebernahme}
          audienceKey={active?.audienceKey ?? ''}
          onClose={() => setUebernahme(null)}
          onDone={(ergebnis) => {
            setUebernahme(null);
            setUebernommen(ergebnis);
            if (activeId) void loadBoard(activeId);
          }}
        />
      )}

      {verloren && (
        <LostDealNotesDialog
          dealId={verloren.deal.id}
          dealTitle={verloren.deal.title}
          stageName={verloren.stage.name}
          notes={verloren.deal.notes}
          frischVerloren
          onClose={() => setVerloren(null)}
          onSaved={() => {
            setVerloren(null);
            if (activeId) void loadBoard(activeId);
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Deal „${deleting.title}“ löschen?`}
          confirmLabel="Löschen"
          destructive
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await crm.pipelines.deleteDeal(deleting.id);
            await afterChange();
          }}
        >
          <p className="text-sm text-muted-foreground">
            Aufgaben zu diesem Deal bleiben bestehen, verlieren aber den Bezug.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function DealCard({
  deal,
  dragging,
  onDragStart,
  onDragEnd,
  onEdit,
  onDelete,
  onTakeover,
}: {
  deal: Deal;
  /** Nur auf der Zusage-Spalte gesetzt. */
  onTakeover?: (() => void) | undefined;
  dragging: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const navigate = useNavigate();

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`group cursor-grab rounded-md border bg-card p-3 shadow-sm active:cursor-grabbing ${
        dragging ? 'opacity-40' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <button className="text-left text-sm font-medium hover:underline" onClick={onEdit}>
          {deal.title}
        </button>
        <button
          className="opacity-0 transition-opacity group-hover:opacity-100"
          aria-label={`${deal.title} löschen`}
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5 text-destructive" />
        </button>
      </div>

      {deal.valueCents > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          {formatEuro(deal.valueCents)}
        </p>
      )}

      {deal.contactId && (
        <button
          className="mt-1 text-xs text-muted-foreground hover:underline"
          onClick={() => navigate(`/kontakte/${deal.contactId}`)}
        >
          Zum Kontakt
        </button>
      )}

      {onTakeover && (
        <Button
          variant="outline"
          size="sm"
          className="mt-2 w-full"
          onClick={onTakeover}
        >
          <Handshake className="mr-1.5 h-3.5 w-3.5" />
          Als Kunde übernehmen
        </Button>
      )}
    </div>
  );
}
